/**
 * Knows when someone starts and stops talking, from the microphone's loudness alone.
 * The first moments of listening measure the room, so a noisy room needs a louder voice.
 */
export interface SpeechDetectorOptions {
  /** Silence after speech that ends a turn. */
  endSilenceMs?: number;
  /** Loudness that must last this long before it counts as speech, not a click. */
  startMs?: number;
  /** A turn never runs longer than this; the maker keeps talking in the next turn. */
  maxTurnMs?: number;
  /** Time spent measuring the room before listening for speech. */
  calibrateMs?: number;
  /** A turn needs at least this much actual voice; a tick, a breath or an echo is dropped. */
  minVoicedMs?: number;
}

/** `noise`: something was heard, but too little voice to be a turn; nothing is sent. */
export type SpeechEvent = "start" | "end" | "noise" | null;

export class SpeechDetector {
  private readonly endSilenceMs: number;
  private readonly startMs: number;
  private readonly maxTurnMs: number;
  private readonly calibrateMs: number;
  private beganAt = -1;
  private noise = 0;
  private samples = 0;
  private loudSince = -1;
  private quietSince = -1;
  private speakingSince = -1;
  private voicedMs = 0;
  private lastSample = -1;
  private readonly minVoicedMs: number;

  constructor(options: SpeechDetectorOptions = {}) {
    this.endSilenceMs = options.endSilenceMs ?? 1100;
    this.startMs = options.startMs ?? 160;
    this.maxTurnMs = options.maxTurnMs ?? 90_000;
    this.calibrateMs = options.calibrateMs ?? 400;
    this.minVoicedMs = options.minVoicedMs ?? 450;
  }

  get speaking(): boolean {
    return this.speakingSince >= 0;
  }

  /** The loudness a voice must reach in this room. */
  get threshold(): number {
    return Math.max(0.06, Math.min(0.22, this.noise * 2.6));
  }

  /** A new conversation: measure the room again. */
  reset(): void {
    this.beganAt = -1;
    this.noise = 0;
    this.samples = 0;
    this.rearm();
  }

  /**
   * The next turn in the same conversation. The room is already known: measuring it again
   * right after Iris speaks would mistake the maker's quick answer for background noise.
   */
  rearm(): void {
    this.loudSince = -1;
    this.quietSince = -1;
    this.speakingSince = -1;
    this.voicedMs = 0;
    this.lastSample = -1;
  }

  /** Feed one loudness sample (0..1) taken at `now` milliseconds. */
  feed(level: number, now: number): SpeechEvent {
    if (this.beganAt < 0) this.beganAt = now;
    if (now - this.beganAt < this.calibrateMs) {
      this.samples += 1;
      this.noise += (level - this.noise) / this.samples;
      return null;
    }
    const loud = level >= this.threshold;
    if (!this.speaking) {
      if (!loud) {
        this.loudSince = -1;
        // The room slowly changes; learn only from real quiet, never from soft speech.
        if (level < this.threshold * 0.5) this.noise = this.noise * 0.995 + level * 0.005;
        return null;
      }
      if (this.loudSince < 0) this.loudSince = now;
      if (now - this.loudSince < this.startMs) return null;
      this.speakingSince = this.loudSince;
      this.voicedMs = now - this.loudSince;
      this.lastSample = now;
      this.quietSince = -1;
      return "start";
    }
    if (loud) {
      if (this.lastSample >= 0) this.voicedMs += now - this.lastSample;
      this.quietSince = -1;
    } else if (this.quietSince < 0) this.quietSince = now;
    this.lastSample = now;
    const silentLongEnough = this.quietSince >= 0 && now - this.quietSince >= this.endSilenceMs;
    if (silentLongEnough || now - this.speakingSince >= this.maxTurnMs) {
      const enough = this.voicedMs >= this.minVoicedMs;
      this.rearm();
      return enough ? "end" : "noise";
    }
    return null;
  }
}
